import { clsx } from 'clsx';
import { Image as ImageIcon, Video, Layers, Link as LinkIcon } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CreativeAtomData {
  id: string;
  thumbnail_url?: string;
  image_url?: string;
  media_type?: string;
  primary_text?: string;
  hook_type?: string;
  tone?: string;
  visual_style?: string;
  parent_brief_id?: string;
  spend?: number;
  ltv_roas?: number;
  impressions?: number;
  effectiveness_score: number;
  effectiveness_rank: number;
}

export interface CreativeAtomCardProps {
  creative: CreativeAtomData;
  onClick?: () => void;
  /** 0..100 — drives the top-edge gradient color. */
  effectivenessPercentile: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(n?: number): string {
  if (n === undefined || n === null || !Number.isFinite(n)) return '—';
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

function formatRoas(n?: number): string {
  if (n === undefined || n === null || !Number.isFinite(n)) return '—';
  return `${n.toFixed(1)}x`;
}

function gradientForPercentile(pct: number): string {
  // 0..100 → teal (high) → amber → slate (low)
  if (pct >= 66) return 'linear-gradient(to right, #14b8a6, #2dd4bf)';
  if (pct >= 33) return 'linear-gradient(to right, #f59e0b, #fbbf24)';
  return 'linear-gradient(to right, #94a3b8, #cbd5e1)';
}

function MediaTypeBadge({ mediaType }: { mediaType?: string }) {
  const mt = (mediaType ?? 'unknown').toLowerCase();
  let Icon = ImageIcon;
  let label = 'Image';
  if (mt === 'video') {
    Icon = Video;
    label = 'Video';
  } else if (mt === 'carousel' || mt === 'slideshow') {
    Icon = Layers;
    label = mt === 'carousel' ? 'Carousel' : 'Slideshow';
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-black/55 text-white backdrop-blur-sm px-1.5 py-0.5 rounded-full">
      <Icon size={10} />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// CreativeAtomCard
// ---------------------------------------------------------------------------

export function CreativeAtomCard({
  creative,
  onClick,
  effectivenessPercentile,
}: CreativeAtomCardProps) {
  const thumb = creative.thumbnail_url || creative.image_url;
  const formulaParts = [creative.hook_type, creative.tone, creative.visual_style].filter(
    Boolean,
  );

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-white rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-sm transition-all w-full overflow-hidden flex flex-col"
    >
      {/* Top edge effectiveness gradient (4px) */}
      <div className="h-1" style={{ background: gradientForPercentile(effectivenessPercentile) }} />

      {/* Thumbnail (4/5 portrait) */}
      <div className="relative w-full bg-slate-100" style={{ aspectRatio: '4 / 5' }}>
        {thumb ? (
          <img
            src={thumb}
            alt={creative.primary_text ?? 'Ad creative'}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-slate-300">
            <ImageIcon size={36} />
          </div>
        )}
        <div className="absolute top-2 right-2">
          <MediaTypeBadge mediaType={creative.media_type} />
        </div>
      </div>

      {/* Body */}
      <div className="p-3 flex flex-col gap-2 min-w-0">
        <div className="text-[12px] font-medium text-navy-900 line-clamp-2 leading-snug min-h-[2.2em]">
          {creative.primary_text ?? '—'}
        </div>

        {formulaParts.length > 0 && (
          <div className="text-[11px] text-slate-500 truncate">
            {formulaParts.map((p, i) => (
              <span key={`${p}-${i}`}>
                {i > 0 && <span className="text-slate-300 mx-1">·</span>}
                {p}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <span className="font-semibold text-navy-900">ROAS {formatRoas(creative.ltv_roas)}</span>
          <span className="text-slate-300">·</span>
          <span>{formatCurrency(creative.spend)}</span>
          <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden ml-2">
            <div
              className={clsx(
                'h-full rounded-full',
                effectivenessPercentile >= 66
                  ? 'bg-teal-400'
                  : effectivenessPercentile >= 33
                    ? 'bg-amber-400'
                    : 'bg-slate-400',
              )}
              style={{ width: `${Math.max(4, Math.min(100, effectivenessPercentile))}%` }}
            />
          </div>
        </div>

        {creative.parent_brief_id && (
          <div className="flex items-center gap-1 text-[10px] text-slate-400 truncate">
            <LinkIcon size={10} />
            <span className="font-mono truncate">{creative.parent_brief_id}</span>
          </div>
        )}
      </div>
    </button>
  );
}
